import { Suspense, lazy, useEffect, useState, type FormEvent } from "react";
import { errorMessage } from "../api/client";
import opoLogo from "../assets/opo-logo.png";
import { GlowingShadow } from "../components/GlowingShadow";
import { Button, ErrorNote, Field, Input, Spinner } from "../components/ui";
import { useAuth } from "./AuthContext";

const EMAIL_KEY = "backoffice.lastEmail";

// Three.js is ~600kB — split into its own chunk so it never blocks the form
// itself, which is the actual critical path. If it's slow to arrive (or
// WebGL is unavailable), the form still works with a plain dark background.
const DotGridBackground = lazy(() => import("./DotGridBackground"));

/**
 * Sign-in screen — the web counterpart of the desktop login dialog, minus the
 * server-URL field (a deployed web app is already served from the right place)
 * and minus "Use Local Database" (a browser has no local SQLite file).
 *
 * This screen keeps a fixed dark identity regardless of the app's light/dark
 * theme toggle (same idea as most products' sign-in screens), and there's
 * intentionally no social login or self-service sign-up here: the backend
 * only supports email+password against operators created in Settings →
 * Operators (see backend/auth.py) — there's no OAuth and no open registration.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The Input/Field/Button primitives read theme CSS variables, which flip
  // with the operator's saved light/dark preference — but this screen keeps
  // a fixed dark identity, so it forces data-theme for as long as it's
  // mounted and restores whatever was there before on unmount. Bypasses
  // applyTheme() deliberately: this must not overwrite the saved preference.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.dataset.theme;
    root.dataset.theme = "dark";
    return () => {
      if (previous) root.dataset.theme = previous;
      else delete root.dataset.theme;
    };
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      // Remembered for next time, exactly as the desktop dialog does. The
      // password never is.
      localStorage.setItem(EMAIL_KEY, email.trim());
    } catch (cause) {
      setError(errorMessage(cause));
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-full items-center justify-center overflow-hidden bg-[#060606] px-4 py-10">
      <Suspense fallback={null}>
        <DotGridBackground />
      </Suspense>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(circle at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 100%)",
        }}
      />

      <div className="relative z-10 w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={opoLogo} alt="OPO" className="mb-4 h-9 w-auto" />
          <h1 className="text-xl font-semibold tracking-tight text-white">Sign in to BackOffice</h1>
          <p className="mt-1 text-sm text-white/50">OPO Compensation Management</p>
        </div>

        <div className="rounded-xl border border-white/10 bg-[#111511] p-6 shadow-2xl">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Field label="Email">
              <Input
                type="email"
                name="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@opo.com"
              />
            </Field>

            <Field label="Password">
              <Input
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {error ? <ErrorNote>{error}</ErrorNote> : null}

            <GlowingShadow>
              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={busy || !email || !password}
              >
                {busy ? <Spinner /> : null}
                {busy ? "Signing in…" : "Sign In"}
              </Button>
            </GlowingShadow>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-white/40">
          Use the email and password registered for you in Settings → Operators.
        </p>
      </div>
    </div>
  );
}
